import React, { useState, useEffect, useCallback, useRef, useMemo, memo } from 'react';
import LoadingSpinner from '../components/LoadingSpinner';
import Comments from '../components/Comments';
import { GroundBaitImage } from '../components/GameImages';
import '../css.css';

const TABLES = [
  { key: 'groundbaits_boil', label: '보일리 번역' },
  { key: 'groundbaits_nature', label: '천연미끼 번역' },
  { key: 'groundbaits_ingredient', label: '밑밥 번역' },
];

const HEADERS = [
  { key: 'filename', label: '사진' },
  { key: 'maker', label: '제조사' },
  { key: 'type', label: '미끼 종류' },
  { key: 'name', label: '이름' },
  { key: 'en', label: '영어' },
  { key: 'ru', label: '러시아어' },
  { key: 'cn', label: '중국어(간체)' },
  { key: 'note', label: '비고' },
];

// 밑밥 테이블 행 컴포넌트 - 메모이제이션
const GroundBaitRow = memo(({ row, headers }) => {
  return (
    <tr>
      <td>
        {row.filename ? (
          <GroundBaitImage
            filename={row.filename}
            alt={row.name}
            className="groundbait-image"
            onError={() => {
              console.warn('Failed to load groundbait image:', row.filename);
            }}
          />
        ) : null}
      </td>
      <td>{row.maker}</td>
      <td>{row.type}</td>
      <td>{row.name}</td>
      <td>{row.en}</td>
      <td>{row.ru}</td>
      <td>{row.cn}</td>
      <td>{row.note}</td>
    </tr>
  );
}, (prevProps, nextProps) => {
  return prevProps.row === nextProps.row;
});

const Page1 = () => {
  const [selectedTable, setSelectedTable] = useState(TABLES[0].key);
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [isHeaderFixed, setIsHeaderFixed] = useState(false);
  const [resizeKey, setResizeKey] = useState(0); // 리사이즈 시 강제 리렌더링용
  
  const tableRef = useRef(null);
  const headerRef = useRef(null);
  const cloneHeaderRef = useRef(null);

  // 스크롤 이벤트 핸들러
  useEffect(() => {
    const handleScroll = () => {
      if (!tableRef.current || !headerRef.current) return;

      const tableRect = tableRef.current.getBoundingClientRect();
      const headerHeight = 65; // 페이지 헤더 높이

      // 테이블이 헤더 아래로 올라갔을 때 고정
      if (tableRect.top <= headerHeight && tableRect.bottom > headerHeight) {
        setIsHeaderFixed(true);
      } else {
        setIsHeaderFixed(false);
      }
    };

    const handleResize = () => {
      setResizeKey(prev => prev + 1); // 리사이즈 시 key 변경으로 강제 리렌더링
      handleScroll();
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleResize, { passive: true });
    handleScroll(); // 초기 상태 체크
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleResize);
    };
  }, []);
  
  // 검색 필터링 함수
  const handleSearch = useCallback((term) => {
    setSearchTerm(term);
    if (!term.trim()) {
      setFilteredData(data);
      return;
    }

    const lowercaseSearchTerm = term.toLowerCase();
    const filtered = data.filter(item => 
      (item.name && item.name.toLowerCase().includes(lowercaseSearchTerm)) ||
      (item.en && item.en.toLowerCase().includes(lowercaseSearchTerm)) ||
      (item.ru && item.ru.toLowerCase().includes(lowercaseSearchTerm)) ||
      (item.cn && item.cn.toLowerCase().includes(lowercaseSearchTerm)) ||
      (item.maker && item.maker.toLowerCase().includes(lowercaseSearchTerm)) ||
      (item.type && item.type.toLowerCase().includes(lowercaseSearchTerm))
    );
    setFilteredData(filtered);
  }, [data]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const response = await fetch(`/api/groundbaits/${selectedTable}`);
        if (!response.ok) throw new Error('Network response was not ok');
        
        const fetchedData = await response.json();
        setData(fetchedData);
        setFilteredData(fetchedData);
        setSearchTerm('');
      } catch (error) {
        console.error('Error fetching data:', error);
        setData([]);
        setFilteredData([]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [selectedTable]);

  // 테이블 렌더링 메모이제이션
  const renderTable = useMemo(() => {
    if (filteredData.length === 0 && searchTerm) {
      return (
        <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
          검색 결과가 없습니다.
        </div>
      );
    }
    
    if (filteredData.length === 0) return null;

    return (
      <>
        {/* 고정 헤더 (스크롤 시 표시) */}
        {isHeaderFixed && tableRef.current && (
          <div 
            key={resizeKey}
            ref={cloneHeaderRef}
            className="fixed-table-header"
            style={{
              position: 'fixed',
              top: '65px',
              left: tableRef.current.getBoundingClientRect().left + 'px',
              zIndex: 99,
              backgroundColor: 'var(--bg-primary, white)',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
            }}
          >
            <table className="oil-table groundbait-table-fixed" style={{ 
              marginBottom: 0,
              width: tableRef.current.offsetWidth + 'px',
              tableLayout: 'fixed'
            }}>
              <thead>
                <tr>
                  {HEADERS.map((h) => (
                    <th key={h.key}>{h.label}</th>
                  ))}
                </tr>
              </thead>
            </table>
          </div>
        )}

        {/* 실제 테이블 */}
        <div className="table-wrapper">
          <table ref={tableRef} className="oil-table groundbait-table-fixed" style={{ 
            minWidth: '1200px',
            tableLayout: 'fixed'
          }}>
            <thead ref={headerRef}>
              <tr>
                {HEADERS.map((h) => (
                  <th key={h.key}>{h.label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filteredData.map((row, idx) => (
                <GroundBaitRow 
                  key={row.id || idx} 
                  row={row}
                  headers={HEADERS}
                />
              ))}
            </tbody>
          </table>
        </div>
      </>
    );
  }, [filteredData, searchTerm, isHeaderFixed, resizeKey]);

  return (
    <>
      <h1 className="page-title">미끼 밑밥 번역</h1>
      <div className="skill-container">
        <div className="skill-filter-buttons">
          {TABLES.map((tbl) => (
            <button
              key={tbl.key}
              onClick={() => setSelectedTable(tbl.key)}
              className={selectedTable === tbl.key ? 'active' : ''}
            >
              {tbl.label}
            </button>
          ))}
        </div>
        {loading ? (
          <div className="spinner-container">
            <LoadingSpinner height={80} width={80} color="#4fa94d" visible={true} />
          </div>
        ) : (
          <div className="page-content">
            <div className="search-container" style={{ 
              marginBottom: '20px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              textAlign: 'center'
            }}>
              <input
                type="text"
                placeholder="이름, 영어, 러시아어, 중국어, 제조사, 미끼 종류로 검색"
                value={searchTerm}
                onChange={(e) => handleSearch(e.target.value)}
                className="search-input"
                style={{
                  width: '100%',
                  maxWidth: '500px',
                  padding: '12px 16px',
                  fontSize: '14px',
                  border: '1px solid #ddd',
                  borderRadius: '8px',
                  outline: 'none',
                  transition: 'border-color 0.3s ease'
                }}
                onFocus={(e) => e.target.style.borderColor = '#4fa94d'}
                onBlur={(e) => e.target.style.borderColor = '#ddd'}
              />
              {searchTerm && (
                <div style={{ marginTop: '8px', fontSize: '14px', color: '#666' }}>
                  {filteredData.length}개의 결과가 찾아졌습니다.
                </div>
              )}
            </div>
            
            {/* 테이블 렌더링 */}
            {renderTable}
          </div>
        )}
      </div>
      
      <Comments pageName="groundbaits" />
    </>
  );
};

export default Page1;
